/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import adminApi from '../../services/adminApi';
import AdminLayout from '../../components/AdminLayout';
import './AdapterDetails.css';

const AdapterDetails = () => {
  const { adapterId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();

  const [adapter, setAdapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /**
   * Load adapter details
   */
  useEffect(() => {
    loadAdapter();
  }, [adapterId]);

  const loadAdapter = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await adminApi.getAdapter(adapterId);
      setAdapter(response.adapter || response);
    } catch (err) {
      console.error('Load adapter error:', err);
      setError(err.response?.data?.error || 'Failed to load adapter details');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle activate adapter
   */
  const handleActivate = async () => {
    if (!window.confirm('Activate this adapter? It will be available for inference.')) {
      return;
    }

    try {
      await adminApi.activateAdapter(adapterId);
      await loadAdapter(); // Reload adapter
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to activate adapter');
    }
  };

  /**
   * Handle deactivate adapter
   */
  const handleDeactivate = async () => {
    if (!window.confirm('Deactivate this adapter? It will no longer be available for inference.')) {
      return;
    }

    try {
      await adminApi.deactivateAdapter(adapterId);
      await loadAdapter(); // Reload adapter
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to deactivate adapter');
    }
  };

  /**
   * Format date
   */
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Format file size
   */
  const formatSize = (bytes) => {
    if (!bytes) return '--';
    const mb = bytes / (1024 * 1024);
    if (mb > 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <AdminLayout showBackButton={true} backPath="/admin/training/adapters">
        <div className="adapter-details loading">
          <div className="spinner-large"></div>
          <p>Loading adapter details...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error || !adapter) {
    return (
      <AdminLayout showBackButton={true} backPath="/admin/training/adapters">
        <div className="adapter-details error">
          <div className="error-icon">⚠️</div>
          <h2>Error Loading Adapter</h2>
          <p>{error || 'Adapter not found'}</p>
          <button
            className="primary-button"
            onClick={() => navigate('/admin/training/adapters')}
          >
            Back to Adapters
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout showBackButton={true} backPath="/admin/training/adapters">
      <div className="adapter-details">
        {/* Header */}
        <div className="page-header">
          <div className="header-left">
            <div className="domain-badge">
              <span className="domain-name">{adapter.domain}</span>
              {adapter.subDomain && (
                <span className="subdomain-name"> / {adapter.subDomain}</span>
              )}
            </div>
            <h1>Adapter Details</h1>
            <div className="status-row">
              <span className={`status-badge ${adapter.active ? 'badge-active' : 'badge-inactive'}`}>
                {adapter.active ? 'Active' : 'Inactive'}
              </span>
              <span className={`status-badge badge-${adapter.status}`}>
                {adapter.status}
              </span>
            </div>
          </div>
          <div className="header-right">
            {adapter.active ? (
              hasPermission('adapter.deactivate') && (
                <button
                  className="deactivate-button"
                  onClick={handleDeactivate}
                >
                  Deactivate
                </button>
              )
            ) : (
              hasPermission('adapter.activate') && (
                <button
                  className="activate-button"
                  onClick={handleActivate}
                >
                  Activate
                </button>
              )
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="details-content">
          {/* Basic Information */}
          <div className="info-section">
            <h2>Basic Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Adapter Name</span>
                <span className="info-value">{adapter.name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Domain</span>
                <span className="info-value">{adapter.domain}</span>
              </div>
              {adapter.subDomain && (
                <div className="info-item">
                  <span className="info-label">Subdomain</span>
                  <span className="info-value">{adapter.subDomain}</span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">Version</span>
                <span className="info-value">v{adapter.version}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Status</span>
                <span className="info-value">{adapter.status}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Created</span>
                <span className="info-value">{formatDate(adapter.createdAt)}</span>
              </div>
              {adapter.createdBy && (
                <div className="info-item">
                  <span className="info-label">Created By</span>
                  <span className="info-value">
                    {adapter.createdBy.fullName || adapter.createdBy.username || 'Unknown'}
                  </span>
                </div>
              )}
              {adapter.size && (
                <div className="info-item">
                  <span className="info-label">Model Size</span>
                  <span className="info-value">{formatSize(adapter.size)}</span>
                </div>
              )}
              {adapter.modelSize && (
                <div className="info-item">
                  <span className="info-label">Total Size</span>
                  <span className="info-value">{formatSize(adapter.modelSize)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Model Information */}
          <div className="info-section">
            <h2>Model Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Base Model</span>
                <span className="info-value">{adapter.baseModel || '--'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Adapter Path</span>
                <span className="info-value code">{adapter.path || '--'}</span>
              </div>
            </div>
          </div>

          {/* Training Information */}
          {adapter.trainedOn && (
            <div className="info-section">
              <h2>Training Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Record Count</span>
                  <span className="info-value">{adapter.trainedOn.recordCount || '--'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Data Version</span>
                  <span className="info-value">{adapter.trainedOn.dataVersion || '--'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Performance Metrics */}
          {adapter.quality && (
            <div className="info-section">
              <h2>Performance Metrics</h2>
              <div className="metrics-grid">
                {adapter.quality.testAccuracy !== undefined && (
                  <div className="metric-card">
                    <div className="metric-label">Test Accuracy</div>
                    <div className="metric-value">
                      {(adapter.quality.testAccuracy * 100).toFixed(2)}%
                    </div>
                  </div>
                )}
                {adapter.quality.overallScore !== undefined && (
                  <div className="metric-card">
                    <div className="metric-label">Overall Score</div>
                    <div className="metric-value">
                      {adapter.quality.overallScore.toFixed(0)}/100
                    </div>
                  </div>
                )}
                {adapter.quality.avgConfidence !== undefined && (
                  <div className="metric-card">
                    <div className="metric-label">Avg Confidence</div>
                    <div className="metric-value">
                      {(adapter.quality.avgConfidence * 100).toFixed(1)}%
                    </div>
                  </div>
                )}
                {adapter.quality.testsPassed !== undefined && adapter.quality.testsTotal !== undefined && (
                  <div className="metric-card">
                    <div className="metric-label">Tests Passed</div>
                    <div className="metric-value">
                      {adapter.quality.testsPassed}/{adapter.quality.testsTotal}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Hyperparameters */}
          {adapter.hyperparameters && Object.keys(adapter.hyperparameters).length > 0 && (
            <div className="info-section">
              <h2>Hyperparameters</h2>
              <div className="hyperparams-grid">
                {Object.entries(adapter.hyperparameters).map(([key, value]) => (
                  <div key={key} className="info-item">
                    <span className="info-label">{key}</span>
                    <span className="info-value code">
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Training Job Link */}
          {adapter.trainingJobId && (
            <div className="info-section">
              <h2>Related Training Job</h2>
              <button
                className="job-link-button"
                onClick={() => navigate(`/admin/training/jobs/${adapter.trainingJobId.jobId || adapter.trainingJobId}`)}
              >
                View Training Job Details →
              </button>
            </div>
          )}

          {/* Metadata */}
          {adapter.metadata && (
            <div className="info-section">
              <h2>Additional Metadata</h2>
              <div className="info-grid">
                {adapter.metadata.syncedFromFilesystem && (
                  <div className="info-item">
                    <span className="info-label">Source</span>
                    <span className="info-value">Synced from filesystem</span>
                  </div>
                )}
                {adapter.metadata.syncDate && (
                  <div className="info-item">
                    <span className="info-label">Sync Date</span>
                    <span className="info-value">{formatDate(adapter.metadata.syncDate)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdapterDetails;
